import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Check, Download, Send, Loader2, Zap,
  ShieldCheck, Bell, EyeOff, ChevronDown, ChevronUp, Coins,
  Sparkles, Terminal, FileCode, Cpu, BookOpen, AlertTriangle,
  CircleDot, Radio, Pencil, X as XIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * Workspace — Phase 3.2 (LuxAlgo Quant 30/40/30 Refactor)
 *
 * Left column  (30 %): Prompt & Chat rail — chat trail, auto-inject
 *                      toggles, prompt textarea, primary Compile CTA.
 * Center column (40 %): Monaco Editor — file chip header + full-height
 *                      Pine v6 source, read-only.
 * Right column (30 %): RAG Diagnostics — compiler output terminal
 *                      (upper), knowledge / concept pills (lower).
 *
 * Color contract (unchanged from previous Beam-AI refresh):
 *   base bg:      #030712   card fill:  #09090B   card border: #1F2937
 *   accent:       #0EA5E9   accent-2:   #A855F7   text:        #F8FAFC
 *   amber pill:   #F59E0B / #FBBF24   error:       #EF4444
 */
export default function Workspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();

  const [script, setScript] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [copied, setCopied] = useState(false);
  const [knowledgePills, setKnowledgePills] = useState([]);

  // Phase 3.2-Hotfix — direct Monaco editor handle so we can push a
  // new code payload imperatively via `editor.setValue(...)` on top
  // of the controlled `value` prop. Belt-and-suspenders: if a
  // concurrent React render on knowledge_pills / messages ever
  // interleaves badly with the `value` prop diff, the imperative
  // update ensures the code canvas still catches the new bytes.
  const editorRef = useRef(null);
  const codeSyncRef = useRef({ lastCode: "" });

  const handleEditorMount = useCallback((editor) => {
    editorRef.current = editor;
  }, []);

  /**
   * Extract a Pine v6 body from a markdown fenced block. If the
   * backend response omits `data.script.code` but the assistant
   * content carries ```pinescript ... ``` (or ```pine / ```
   * unlabelled), we fall back to the extraction so the editor is
   * never left blank when the LLM answered inside a fenced block.
   */
  const extractPineFromFence = useCallback((text) => {
    if (!text || typeof text !== "string") return "";
    const patterns = [
      /```pinescript\s*\n([\s\S]*?)\n```/i,
      /```pine\s*\n([\s\S]*?)\n```/i,
      /```\s*\n(\/\/@version=[\s\S]*?)\n```/i,
    ];
    for (const re of patterns) {
      const m = text.match(re);
      if (m && m[1] && m[1].trim().length > 0) return m[1];
    }
    return "";
  }, []);

  /**
   * Single authoritative writer for the code canvas. Guarantees
   * that (a) React state is updated, (b) the Monaco instance is
   * imperatively pushed if the model actually diverged from the
   * new payload, and (c) knowledge_pills / messages updates cannot
   * "shadow" a code update because this runs BEFORE those setters.
   */
  const pushCodeToCanvas = useCallback((newCode, nextScript) => {
    const cleanCode = newCode ?? "";
    codeSyncRef.current.lastCode = cleanCode;
    if (nextScript) {
      setScript((prev) => ({
        ...(prev || {}),
        ...nextScript,
        code: cleanCode || nextScript.code || (prev && prev.code) || "",
      }));
    } else {
      setScript((prev) => (prev ? { ...prev, code: cleanCode } : prev));
    }
    // Imperative fallback — Monaco's controlled-value diff is normally
    // enough, but a concurrent state batch on the same tick has been
    // reported to drop the update. Force the model to the new value.
    try {
      const editor = editorRef.current;
      const model = editor?.getModel?.();
      if (model && model.getValue() !== cleanCode) {
        model.setValue(cleanCode);
      }
    } catch (_e) { /* Monaco not mounted yet — controlled prop will hydrate on mount */ }
  }, []);

  const [autoInject, setAutoInject] = useState({
    risk_mgmt: null, alerts: null, lookahead: null,
  });
  const [savingLayout, setSavingLayout] = useState(false);
  const saveLayoutRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: s }, { data: chat }] = await Promise.all([
          api.get(`/scripts/${id}`),
          api.get(`/scripts/${id}/chat`),
        ]);
        if (cancelled) return;
        setScript(s);
        setMessages(chat?.messages || []);
        // Phase 3.2 — hydrate the amber Knowledge Context row from the
        // latest assistant message that carries any concept pills.
        const hist = chat?.messages || [];
        for (let i = hist.length - 1; i >= 0; i--) {
          const m = hist[i];
          if (m.role !== "assistant") continue;
          const pills = Array.isArray(m.knowledge_pills) ? m.knowledge_pills : [];
          if (pills.length) { setKnowledgePills(pills); break; }
        }
        const ai = s?.workspace_layout?.auto_inject || {};
        setAutoInject({
          risk_mgmt: ai.risk_mgmt ?? null,
          alerts:    ai.alerts    ?? null,
          lookahead: ai.lookahead ?? null,
        });
      } catch (e) {
        toast.error("Failed to load workspace: " + (e?.response?.data?.detail || e.message));
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Debounced auto-save of workspace_layout.
  useEffect(() => {
    if (!script) return;
    if (saveLayoutRef.current) clearTimeout(saveLayoutRef.current);
    saveLayoutRef.current = setTimeout(async () => {
      setSavingLayout(true);
      try {
        await api.put(`/scripts/${id}/workspace-layout`, {
          workspace_layout: {
            auto_inject: autoInject,
            blocks: [{ kind: "full_script" }, { kind: "auto_inject_toggles" }],
            columns: null,
          },
        });
      } catch (_e) { /* silent */ }
      finally { setSavingLayout(false); }
    }, 400);
    return () => clearTimeout(saveLayoutRef.current);
  }, [autoInject, id, script]);

  const latestAssistantWithFindings = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "assistant") continue;
      const hasFindings =
        (Array.isArray(m.lint_findings) && m.lint_findings.length) ||
        (Array.isArray(m.lint_warnings) && m.lint_warnings.length) ||
        (Array.isArray(m.lint_errors) && m.lint_errors.length);
      const hasKnowledge = Array.isArray(m.knowledge_tags) && m.knowledge_tags.length;
      if (hasFindings || m.auto_fix_meta || hasKnowledge) return m;
    }
    return null;
  }, [messages]);

  const sendChat = async () => {
    const text = prompt.trim();
    if (!text || sending) return;
    setSending(true);
    setPrompt("");
    const optimisticUser = {
      id: `tmp-${Date.now()}`, role: "user", content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticUser]);
    try {
      const { data } = await api.post(`/scripts/${id}/chat`, { message: text });
      setMessages((prev) => {
        const withoutTmp = prev.filter((m) => m.id !== optimisticUser.id);
        const enrichedAsst = data.assistant_message
          ? {
              ...data.assistant_message,
              lint_warnings: data.lint_warnings || data.assistant_message.lint_warnings || [],
              lint_errors:   data.lint_errors   || data.assistant_message.lint_errors   || [],
              lint_findings: data.lint_findings || data.assistant_message.lint_findings || [],
              auto_fix_meta: data.auto_fix_meta || data.assistant_message.auto_fix_meta || null,
              knowledge_tags: data.knowledge_tags || data.assistant_message.knowledge_tags || [],
            }
          : data.assistant_message;
        return [...withoutTmp, data.user_message, enrichedAsst].filter(Boolean);
      });
      if (data.script) {
        // Phase 3.2-STRIP — write code to the canvas FIRST, before
        // touching messages / pills. Preference order for the code
        // payload (each falls back to the next only if empty):
        //   1. data.extracted_code_body                 (root-level, backend authoritative)
        //   2. data.assistant_message.extracted_code_body (per-message backend field)
        //   3. data.script.code                          (revision code — legacy path)
        //   4. extractPineFromFence(assistant_content)   (last-resort client regex)
        let nextCode =
          (typeof data.extracted_code_body === "string" && data.extracted_code_body)
          || (typeof data.assistant_message?.extracted_code_body === "string" && data.assistant_message.extracted_code_body)
          || (data.script.code ?? "")
          || "";
        nextCode = String(nextCode);
        if (!nextCode || nextCode.length === 0) {
          const fenced = extractPineFromFence(
            data.assistant_message?.content || ""
          );
          if (fenced) nextCode = fenced;
        }
        pushCodeToCanvas(nextCode, data.script);
      } else if (
        typeof data.extracted_code_body === "string"
        && data.extracted_code_body.length > 0
      ) {
        // Backend delivered code but no script envelope (e.g. some
        // repair paths). Push the code alone — the current script
        // object stays.
        pushCodeToCanvas(data.extracted_code_body, null);
      }
      // Phase 3.2 — trading-concept pills. Prefer the top-level
      // response envelope (populated on code_update paths); fall back
      // to the assistant_message field which _save_assistant persists
      // for every response type.
      const pills = Array.isArray(data.knowledge_pills)
        ? data.knowledge_pills
        : (data.assistant_message?.knowledge_pills || []);
      setKnowledgePills(pills);
      if (typeof data.credits === "number") refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Chat failed");
      setMessages((prev) => prev.filter((m) => m.id !== optimisticUser.id));
    } finally {
      setSending(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(script?.code || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* clipboard denied */ }
  };

  const downloadPine = () => {
    const blob = new Blob([script?.code || ""], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(script?.title || "script").replace(/[^\w-]+/g, "_")}.pine`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!script) {
    return (
      <div className="relative min-h-screen bg-[#030712] flex items-center justify-center text-slate-400 overflow-hidden">
        <AmbientGlow />
        <Loader2 className="w-5 h-5 animate-spin mr-2 relative" /> Loading workspace…
      </div>
    );
  }

  return (
    <div
      className="relative bg-[#030712] text-slate-100 overflow-hidden flex flex-col lg:flex-row"
      style={{ minHeight: "calc(100vh - 4rem)" }}
      data-testid="workspace-shell"
    >
      <AmbientGlow />
      <PromptRail
        user={user}
        script={script}
        setScript={setScript}
        messages={messages}
        prompt={prompt}
        setPrompt={setPrompt}
        sending={sending}
        sendChat={sendChat}
        autoInject={autoInject}
        setAutoInject={setAutoInject}
        savingLayout={savingLayout}
        nav={nav}
      />
      <CodeCanvas
        script={script}
        copied={copied}
        onCopy={copyCode}
        onDownload={downloadPine}
        onMount={handleEditorMount}
      />
      <DiagnosticsRail
        latestAssistant={latestAssistantWithFindings}
        knowledgePills={knowledgePills}
        script={script}
      />
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* AMBIENT NEON GLOW                                                          */
/* -------------------------------------------------------------------------- */
function AmbientGlow() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
      data-testid="workspace-ambient-glow"
    >
      <div
        className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(168,85,247,0.35) 0%, rgba(168,85,247,0) 65%)",
          filter: "blur(80px)",
        }}
      />
      <div
        className="absolute -bottom-32 -right-32 h-[560px] w-[560px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(14,165,233,0.32) 0%, rgba(14,165,233,0) 65%)",
          filter: "blur(90px)",
        }}
      />
      <div
        className="absolute -top-24 right-1/3 h-[380px] w-[380px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, rgba(124,58,237,0) 70%)",
          filter: "blur(110px)",
        }}
      />
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* LEFT (30 %) — PROMPT & CHAT RAIL                                           */
/* -------------------------------------------------------------------------- */
function PromptRail({
  user, script, setScript, messages, prompt, setPrompt, sending, sendChat,
  autoInject, setAutoInject, savingLayout, nav,
}) {
  const [togglesOpen, setTogglesOpen] = useState(false);
  const planLabel = (user?.plan || "free").toUpperCase();
  const totalCredits = user?.credits ?? 0;

  return (
    <aside
      className="relative z-10 w-full lg:w-[30%] lg:min-w-[340px] lg:max-w-[520px] border-r border-[#1F2937] bg-[#09090B]/85 backdrop-blur-sm flex flex-col"
      data-testid="workspace-prompt-rail"
    >
      {/* Top strip — back link + plan + credits */}
      <div className="px-4 py-3 border-b border-[#1F2937] flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => nav("/scripts")}
          data-testid="workspace-back-to-scripts"
          className="flex items-center gap-1.5 text-slate-400 hover:text-[#0EA5E9] text-[10px] font-mono uppercase tracking-widest transition-colors"
        >
          <ArrowLeft className="w-3 h-3" /> Scripts
        </button>
        <div className="flex items-center gap-2">
          <span
            data-testid="workspace-plan-chip"
            className="px-2 py-0.5 rounded border border-[#0EA5E9]/40 bg-[#0EA5E9]/10 text-[#0EA5E9] text-[10px] font-mono uppercase tracking-wider"
          >
            {planLabel}
          </span>
          <div className="flex items-center gap-1 text-slate-100">
            <Coins className="w-3 h-3 text-[#0EA5E9]" />
            <span
              data-testid="workspace-credits-total"
              className="text-xs font-mono tabular-nums"
            >
              {totalCredits}
            </span>
          </div>
        </div>
      </div>

      {/* Title + subtitle */}
      <div className="px-5 py-4 border-b border-[#1F2937]">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#0EA5E9] mb-1.5 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Workspace · {script.mode}
        </div>
        <EditableTitle
          value={script.title || "Untitled"}
          scriptId={script.id}
          onSaved={(updated) => {
            // Merge server-normalised title + rewritten Pine source
            // back into workspace state so Monaco reflects the
            // renamed indicator(...) declaration.
            if (updated && typeof updated === "object") {
              setScript((prev) => ({ ...(prev || {}), ...updated }));
            }
          }}
        />
        <div className="text-slate-500 text-[11px] font-mono mt-1">
          Auto mode inference · Pine v6
        </div>
      </div>

      {/* Chat trail — scrollable, fills remaining space */}
      <div
        className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2"
        data-testid="workspace-chat-trail"
      >
        {messages.length === 0 && (
          <div className="text-slate-600 italic text-[11px] font-mono leading-relaxed pt-2">
            &gt; Awaiting your first trading intent.
            <br />
            &gt; Try:{" "}
            <button
              type="button"
              onClick={() => setPrompt("Add a bearish divergence exit on RSI < 30")}
              data-testid="workspace-example-prompt"
              className="text-[#0EA5E9] hover:underline"
            >
              "Add a bearish divergence exit on RSI &lt; 30"
            </button>
          </div>
        )}
        {messages.map((m) => (
          <ChatBubble key={m.id} message={m} />
        ))}
      </div>

      {/* Auto-inject overrides — collapsed by default */}
      <div className="border-t border-[#1F2937]">
        <button
          type="button"
          onClick={() => setTogglesOpen((v) => !v)}
          data-testid="workspace-toggles-header"
          className="w-full flex items-center justify-between px-4 py-2 hover:bg-white/[0.02] transition-colors text-[10px] font-mono uppercase tracking-widest text-slate-400"
        >
          <span className="flex items-center gap-1.5">
            <Zap className="w-3 h-3" /> Auto-Inject
            <span className="text-slate-600 normal-case tracking-normal">
              · {savingLayout ? "saving…" : "saved"}
            </span>
          </span>
          {togglesOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {togglesOpen && (
          <div className="px-4 pb-3 space-y-1.5">
            <ToggleRow
              icon={<ShieldCheck className="w-3 h-3" />}
              label="Risk mgmt"
              hint="SL / TP inputs"
              value={autoInject.risk_mgmt}
              onChange={(v) => setAutoInject((a) => ({ ...a, risk_mgmt: v }))}
              testid="ws-toggle-risk-mgmt"
            />
            <ToggleRow
              icon={<Bell className="w-3 h-3" />}
              label="Alerts"
              hint="webhook JSON"
              value={autoInject.alerts}
              onChange={(v) => setAutoInject((a) => ({ ...a, alerts: v }))}
              testid="ws-toggle-alerts"
            />
            <ToggleRow
              icon={<EyeOff className="w-3 h-3" />}
              label="Lookahead"
              hint="barmerge.lookahead_off"
              value={autoInject.lookahead}
              onChange={(v) => setAutoInject((a) => ({ ...a, lookahead: v }))}
              testid="ws-toggle-lookahead"
            />
          </div>
        )}
      </div>

      {/* Prompt input + Compile CTA */}
      <div className="border-t border-[#1F2937] p-4 bg-[#09090B]">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
          <Terminal className="w-3 h-3" /> Trading Input
          <span
            data-testid="workspace-auto-mode-hint"
            className="ml-auto text-slate-600 normal-case tracking-normal italic"
          >
            auto-mode: implicit
          </span>
        </div>
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendChat();
            }
          }}
          placeholder='> Describe your indicator, strategy, or edit…'
          data-testid="workspace-prompt-input"
          className="min-h-[112px] bg-black border-[#1F2937] focus:border-[#0EA5E9]/60 text-slate-100 placeholder:text-slate-600 font-mono text-sm resize-none rounded-md mb-3"
        />
        <Button
          onClick={sendChat}
          disabled={sending || !prompt.trim()}
          data-testid="workspace-compile-cta"
          className="w-full bg-[#0EA5E9] hover:bg-[#38BDF8] text-slate-950 font-semibold shadow-[0_0_24px_-4px_rgba(14,165,233,0.7)] hover:shadow-[0_0_36px_-4px_rgba(14,165,233,0.9)] transition-all"
        >
          {sending
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Compiling…</>
            : <><Cpu className="w-4 h-4 mr-1.5" />Compile Script <Send className="w-3 h-3 ml-2 opacity-60" /></>}
        </Button>
        <div className="text-[10px] font-mono text-slate-600 text-center mt-2">
          Enter to send · Shift+Enter for newline
        </div>
      </div>
    </aside>
  );
}


/* -------------------------------------------------------------------------- */
/* EDITABLE TITLE (Phase 2.1U — inline rename)                                */
/* -------------------------------------------------------------------------- */
/**
 * Inline title editor. Static text by default; a click on the row (or the
 * pencil icon) flips into an <input> that commits on Enter/blur and
 * cancels on Escape. Fires PATCH /api/scripts/{id}/title and merges the
 * server-normalised payload (title with guaranteed "| PineForge" suffix
 * + rewritten Pine declaration) back into the workspace state.
 */
function EditableTitle({ value, scriptId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ""); }, [value]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = async () => {
    const next = (draft || "").trim();
    if (!next || next === value) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/scripts/${scriptId}/title`, { title: next });
      onSaved?.(data);
      toast.success("Title updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
      setDraft(value || "");
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const cancel = () => { setDraft(value || ""); setEditing(false); };

  if (editing) {
    return (
      <div className="flex items-center gap-2" data-testid="workspace-title-editor">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); cancel(); }
          }}
          disabled={saving}
          maxLength={80}
          data-testid="workspace-title-input"
          className="flex-1 min-w-0 bg-black border border-[#0EA5E9]/50 focus:border-[#0EA5E9] focus:outline-none text-white text-2xl font-semibold tracking-tight py-1 px-2 rounded-md"
        />
        {saving && <Loader2 className="w-4 h-4 animate-spin text-[#0EA5E9]" />}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid="workspace-title"
      className="group flex items-center gap-2 text-left w-full min-w-0"
      title="Click to rename"
    >
      <h1 className="text-2xl font-semibold text-white tracking-tight py-1 leading-tight truncate">
        {value || "Untitled"}
      </h1>
      <Pencil
        className="w-3.5 h-3.5 text-slate-500 group-hover:text-[#0EA5E9] transition-colors shrink-0"
        data-testid="workspace-title-edit-btn"
      />
    </button>
  );
}


function ChatBubble({ message }) {  const isUser = message.role === "user";
  return (
    <div
      className={
        "text-[11px] leading-relaxed rounded-md px-2.5 py-1.5 " +
        (isUser
          ? "bg-[#0EA5E9]/8 border border-[#0EA5E9]/25 text-slate-100"
          : "bg-white/[0.02] border border-white/[0.05] text-slate-300")
      }
      data-testid={`chat-bubble-${message.role}`}
    >
      <div
        className={
          "font-mono uppercase text-[9px] tracking-wider mb-0.5 " +
          (isUser ? "text-[#0EA5E9]" : "text-slate-500")
        }
      >
        {isUser ? "You" : "AI"}
      </div>
      <div className="whitespace-pre-wrap break-words">
        {(message.content || "").slice(0, 480)}
        {(message.content || "").length > 480 ? "…" : ""}
      </div>
    </div>
  );
}


function ToggleRow({ icon, label, hint, value, onChange, testid }) {
  const state = value === true ? "on" : value === false ? "off" : "default";
  const next = () => onChange(value === true ? false : value === false ? null : true);
  const styles = {
    on:      { chip: "bg-[#0EA5E9]/15 border-[#0EA5E9]/50 text-[#0EA5E9]", label: "ON" },
    off:     { chip: "bg-red-500/10 border-red-500/40 text-red-300",       label: "OFF" },
    default: { chip: "bg-white/5 border-white/10 text-slate-400",          label: "AUTO" },
  }[state];
  return (
    <div className="flex items-center justify-between gap-2 p-1.5 rounded bg-black border border-[#1F2937]">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-slate-500">{icon}</span>
        <div className="min-w-0">
          <div className="text-[11px] text-slate-100 truncate">{label}</div>
          <div className="text-[9px] text-slate-500 truncate font-mono">{hint}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={next}
        data-testid={testid}
        aria-label={`toggle ${label}`}
        className={`px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border transition-colors ${styles.chip}`}
      >
        {styles.label}
      </button>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* CENTER (40 %) — MONACO CODE CANVAS                                         */
/* -------------------------------------------------------------------------- */
function CodeCanvas({ script, copied, onCopy, onDownload, onMount }) {
  const filename = `${(script?.title || "script").replace(/[^\w-]+/g, "_")}.pine`;
  const lineCount = (script?.code || "").split("\n").length;
  const byteCount = new Blob([script?.code || ""]).size;
  return (
    <section
      className="relative z-10 w-full lg:w-[40%] flex-1 bg-[#09090B]/90 border-x border-[#1F2937] flex flex-col min-w-0"
      data-testid="workspace-code-canvas"
    >
      {/* File chip header */}
      <div className="px-4 py-3 border-b border-[#1F2937] flex items-center gap-3 bg-black/40">
        <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[#0EA5E9]/20 to-[#A855F7]/20 border border-[#0EA5E9]/40 flex items-center justify-center shrink-0">
          <FileCode className="w-4 h-4 text-[#0EA5E9]" />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="text-sm text-slate-100 font-mono truncate"
            data-testid="workspace-file-chip-name"
          >
            {filename}
          </div>
          <div className="text-[10px] text-slate-500 font-mono">
            {lineCount} lines · {(byteCount / 1024).toFixed(1)} KB · Pine v6
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={onCopy}
            data-testid="workspace-copy-code"
            className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-300 hover:text-[#0EA5E9] border border-[#1F2937] hover:border-[#0EA5E9]/40 px-2 py-1 rounded transition-colors"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={onDownload}
            data-testid="workspace-download-code"
            className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-300 hover:text-[#0EA5E9] border border-[#1F2937] hover:border-[#0EA5E9]/40 px-2 py-1 rounded transition-colors"
          >
            <Download className="w-3 h-3" /> .pine
          </button>
          <Link
            to={`/scripts/${script.id}`}
            data-testid="workspace-open-classic-chat"
            className="text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors hidden sm:inline"
            title="Open the classic chat detail view"
          >
            Classic ↗
          </Link>
        </div>
      </div>

      {/* Monaco */}
      <div className="flex-1 min-h-[520px]" data-testid="workspace-monaco-container">
        <Editor
          height="100%"
          defaultLanguage="javascript"
          value={script.code || ""}
          onMount={onMount}
          theme="vs-dark"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
            fontSize: 12,
            lineNumbers: "on",
            renderLineHighlight: "none",
            scrollBeyondLastLine: false,
            wordWrap: "on",
            padding: { top: 12, bottom: 12 },
          }}
        />
      </div>
    </section>
  );
}


/* -------------------------------------------------------------------------- */
/* RIGHT (30 %) — RAG DIAGNOSTICS RAIL                                        */
/* -------------------------------------------------------------------------- */
function DiagnosticsRail({ latestAssistant, knowledgePills, script }) {
  const warnings = latestAssistant?.lint_warnings || [];
  const errors = latestAssistant?.lint_errors || [];
  const findings = latestAssistant?.lint_findings || [];
  const knowledgeTags = latestAssistant?.knowledge_tags || [];

  // Merge lint findings + separate warnings / errors so we render a
  // single unified compile-log list with severity glyphs.
  const merged = useMemo(() => {
    const all = [
      ...findings.map((f) => ({ ...f, source: "lint" })),
      ...errors.map((e) => ({ ...e, severity: "error", source: "err" })),
      ...warnings.map((w) => ({ ...w, severity: "warning", source: "warn" })),
    ].filter((f) => f && f.message);
    return all.sort((a, b) => {
      const sev = (s) => (s === "error" ? 0 : s === "warning" ? 1 : 2);
      const diff = sev(a.severity) - sev(b.severity);
      return diff !== 0 ? diff : (a.line || 0) - (b.line || 0);
    });
  }, [findings, warnings, errors]);

  const nErr = merged.filter((f) => f.severity === "error").length;
  const nWarn = merged.filter((f) => f.severity === "warning").length;
  const nInfo = merged.length - nErr - nWarn;
  const statusChip = nErr > 0
    ? { color: "red", glyph: <CircleDot className="w-3 h-3" />, text: "COMPILE FAILED" }
    : merged.length > 0
      ? { color: "amber", glyph: <AlertTriangle className="w-3 h-3" />, text: "COMPILED · WARNINGS" }
      : { color: "emerald", glyph: <CircleDot className="w-3 h-3" />, text: "COMPILED · CLEAN" };
  const chipCls = {
    red:     "border-red-500/50 bg-red-500/10 text-red-300",
    amber:   "border-amber-500/40 bg-amber-500/10 text-amber-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  }[statusChip.color];

  // Combine backend-classifier tags + trading-concept pills into a
  // single amber-pill row. Concept pills carry a friendly `label`;
  // classifier tags are raw keys we normalise to Title Case.
  const combinedPills = useMemo(() => {
    const seen = new Set();
    const pills = [];
    for (const p of knowledgePills || []) {
      if (!p || !p.key || seen.has(p.key)) continue;
      seen.add(p.key);
      pills.push({ key: p.key, label: p.label || p.key });
    }
    for (const t of knowledgeTags || []) {
      const key = String(t || "").toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      pills.push({
        key,
        label: key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      });
    }
    return pills;
  }, [knowledgePills, knowledgeTags]);

  return (
    <aside
      className="relative z-10 w-full lg:w-[30%] lg:min-w-[300px] lg:max-w-[520px] border-l border-[#1F2937] bg-[#09090B]/85 backdrop-blur-sm flex flex-col"
      data-testid="workspace-diagnostics-rail"
    >
      {/* Status chip */}
      <div className="px-4 py-3 border-b border-[#1F2937] flex items-center justify-between gap-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1.5">
          <Radio className="w-3 h-3" /> Diagnostics
        </div>
        <div
          className={`flex items-center gap-1.5 px-2 py-0.5 rounded border ${chipCls} text-[10px] font-mono uppercase tracking-wider`}
          data-testid="compile-status-chip"
        >
          {statusChip.glyph}
          <span>{statusChip.text}</span>
        </div>
      </div>

      {/* Upper — compiler log terminal */}
      <div
        className="flex-1 min-h-0 overflow-y-auto bg-black/60 border-b border-[#1F2937]"
        data-testid="compiler-log-terminal"
      >
        <div className="px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
            <Terminal className="w-3 h-3" /> Compile Log
            <span className="ml-auto text-slate-600 normal-case tracking-normal">
              {nErr}E · {nWarn}W · {nInfo}i
            </span>
          </div>
          {merged.length === 0 ? (
            <div className="text-[11px] font-mono text-slate-600 italic leading-relaxed">
              &gt; No compile output yet.
              <br />
              &gt; Send a prompt to trigger the Pine v6 generator; any
              lint warnings or auto-fix patches will stream here.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="compiler-log-list">
              {merged.slice(0, 40).map((f, i) => (
                <LogLine key={i} finding={f} />
              ))}
              {merged.length > 40 && (
                <li className="text-[10px] font-mono text-slate-600 italic pt-1">
                  … +{merged.length - 40} more entries suppressed
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      {/* Lower — amber knowledge pills */}
      <div
        className="border-t border-[#1F2937] p-4 bg-[#09090B]"
        data-testid="knowledge-pills-panel"
      >
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
          <BookOpen className="w-3 h-3" /> Knowledge Context
          <span className="ml-auto text-slate-600 normal-case tracking-normal">
            {combinedPills.length} loaded
          </span>
        </div>
        {combinedPills.length === 0 ? (
          <div className="text-[11px] font-mono text-slate-600 italic leading-relaxed">
            &gt; No SMC / ICT / concept keywords detected in your last
            prompt. Try mentioning{" "}
            <span className="text-amber-300/70">FVG</span>,{" "}
            <span className="text-amber-300/70">Order Block</span>,{" "}
            <span className="text-amber-300/70">CHoCH</span> or{" "}
            <span className="text-amber-300/70">VWAP</span>.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="knowledge-pills-list">
            {combinedPills.map((p) => (
              <span
                key={p.key}
                data-testid={`knowledge-pill-${p.key}`}
                className="inline-flex items-center gap-1 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5"
              >
                <Check className="w-2.5 h-2.5" />
                <span className="normal-case tracking-normal">{p.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}


function LogLine({ finding }) {
  const sev = finding.severity || "warning";
  const color = sev === "error"
    ? "text-red-300"
    : sev === "warning" ? "text-amber-300" : "text-slate-400";
  const bg = sev === "error"
    ? "bg-red-500/[0.06] border-l-2 border-red-500/60"
    : sev === "warning" ? "bg-amber-500/[0.04] border-l-2 border-amber-500/50"
    : "bg-white/[0.02] border-l-2 border-slate-600";
  const prefix = sev === "error" ? "ERR" : sev === "warning" ? "WRN" : "INF";
  return (
    <li className={`text-[11px] font-mono px-2 py-1 rounded ${bg}`}>
      <span className={`${color} mr-2`}>{prefix}</span>
      {finding.line != null && (
        <span className="text-slate-500 mr-2">L{finding.line}</span>
      )}
      <span className="text-slate-200">{finding.message}</span>
      {finding.code && (
        <span className="text-slate-600 ml-2">[{finding.code}]</span>
      )}
    </li>
  );
}
